import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../components/AuthProvider';
import { StaticPageHeader } from '../components/StaticPageHeader';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { formatApiErrorDetail } from '../lib/apiError';
import { UpdateProfileForm, ChangePasswordForm } from '../components/AccountSettings';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { track as pixelTrack } from '../lib/metaPixel';
import { HRHubFooter } from '../components/HRHubFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_INFO = {
  standard: { name: 'Standard', price: 39, color: '#1A56E8' },
  pro: { name: 'Pro', price: 79, color: '#F5C100' },
  elite: { name: 'Elite', price: 149, color: '#1A2238' },
};

export const CheckoutPage = () => {
  const { user, loading, authHeader } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tier = (searchParams.get('tier') || '').toLowerCase();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const info = TIER_INFO[tier];

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?next=/checkout?tier=${tier}`);
      return;
    }
    if (!info) {
      navigate('/#memberships');
    }
  }, [loading, user, tier, info, navigate]);

  const startCheckout = async () => {
    setSubmitting(true);
    setError('');
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API}/portal/subscribe`,
        { tier, origin },
        { headers: authHeader() }
      );
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  if (loading || !user || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="checkout-page">
      <StaticPageHeader />
      <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          <Link to="/#memberships" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> Choose a different plan
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Subscribe to {info.name}</h1>
          <p className="text-gray-600 mb-6">Step 2 of 2 — confirm and pay with Stripe.</p>

          <div className="rounded-xl border-2 p-5 mb-6" style={{ borderColor: info.color }}>
            <div className="flex items-baseline justify-between">
              <span className="text-lg font-semibold text-gray-900">{info.name}</span>
              <span>
                <span className="text-4xl font-bold text-gray-900">${info.price}</span>
                <span className="text-gray-500">/mo</span>
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-2">Billed monthly. Cancel anytime from your account page.</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="checkout-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="button"
            disabled={submitting}
            onClick={startCheckout}
            className="w-full text-white font-semibold"
            size="lg"
            style={{ backgroundColor: info.color === '#F5C100' ? '#F5C100' : '#1A56E8', color: info.color === '#F5C100' ? '#1a1a1a' : '#fff' }}
            data-testid="start-checkout-btn"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting to Stripe...</>) : 'Continue to secure checkout'}
          </Button>

          <p className="text-xs text-gray-500 mt-6 text-center">
            All sales are final. Subscription renews automatically each month until you cancel from your account page.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const SubscriptionSuccessPage = () => {
  const { authHeader, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState({ payment_status: 'unpaid', membership_tier: null });
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState('');
  // Fire the Subscribe pixel event exactly once per checkout session.
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session ID.');
      setPolling(false);
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/portal/subscription-status/${sessionId}`, { headers: authHeader() });
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === 'paid' && data.membership_tier) {
          setPolling(false);
          // Consent-gated Subscribe event with the actual tier price.
          if (!trackedRef.current) {
            const tierInfo = TIER_INFO[data.membership_tier];
            if (tierInfo) {
              pixelTrack('Subscribe', {
                value: tierInfo.price,
                currency: 'USD',
                content_name: tierInfo.name,
                predicted_ltv: tierInfo.price * 12,
              });
            }
            trackedRef.current = true;
          }
          await refresh();
          return;
        }
        if (data.status === 'expired') {
          setError('This checkout session expired. Please try again.');
          setPolling(false);
          return;
        }
        attempts += 1;
        if (attempts >= 15) {
          setPolling(false);
          setError("It's taking longer than expected to confirm your payment. Try refreshing in a moment.");
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        if (cancelled) return;
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setPolling(false);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, authHeader, refresh]);

  const paid = status.payment_status === 'paid' && status.membership_tier;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="subscription-success-page">
      <StaticPageHeader />
      <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          {polling && !paid && (
            <>
              <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin mb-4" />
              <h1 className="text-2xl font-bold text-gray-900">Confirming your payment...</h1>
              <p className="text-gray-600 mt-2">This usually takes just a few seconds.</p>
            </>
          )}
          {paid && (
            <>
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900">Welcome to {status.membership_tier?.[0]?.toUpperCase()}{status.membership_tier?.slice(1)}!</h1>
              <p className="text-gray-600 mt-2">Your subscription is now active. Time to explore the HR Hub.</p>
              <Button onClick={() => navigate('/members')} size="lg" className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="go-to-portal-btn">
                Go to my portal
              </Button>
            </>
          )}
          {error && !paid && !polling && (
            <>
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900">Something went off</h1>
              <p className="text-gray-600 mt-2">{error}</p>
              <Button onClick={() => navigate('/#memberships')} variant="outline" className="mt-6 w-full">
                Back to plans
              </Button>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const SubscriptionCanceledPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StaticPageHeader />
      <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="subscription-canceled-page">
          <h1 className="text-2xl font-bold text-gray-900">Checkout canceled</h1>
          <p className="text-gray-600 mt-2">No worries — no charge was made. You can pick a plan whenever you're ready.</p>
          <div className="mt-6 space-y-3">
            <Button onClick={() => navigate('/#memberships')} className="w-full bg-blue-600 hover:bg-blue-700 text-white">View plans</Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">Back to home</Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const AccountBillingPage = () => {
  useDocumentTitle('Account & Billing — HR Hub | DriveHR');
  const { user, authHeader, refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(true);
  const [syncFlash, setSyncFlash] = useState(null);
  const lastSyncAtRef = useRef(0);
  const navigate = useNavigate();

  const runSync = async ({ silent } = { silent: true }) => {
    setSyncing(true);
    setSyncFlash(null);
    try {
      await axios.post(`${API}/portal/sync-subscription`, {}, { headers: authHeader() });
      await refresh();
      lastSyncAtRef.current = Date.now();
      if (!silent) setSyncFlash({ type: 'success', msg: 'Subscription refreshed from Stripe.' });
    } catch (e) {
      if (!silent) {
        setSyncFlash({
          type: 'error',
          msg: formatApiErrorDetail(e.response?.data?.detail) || e.message,
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  // Fallback for portal-initiated plan changes when the Stripe webhook isn't
  // wired yet:
  //  - pull on mount (covers full-page returns from Stripe Customer Portal)
  //  - pull on visibilitychange when the tab regains focus (covers returns
  //    that land on the same already-loaded SPA without a full reload).
  // Throttled to 5s to dedupe rapid tab-flicker.
  useEffect(() => {
    runSync({ silent: true });
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastSyncAtRef.current < 5000) return;
      runSync({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // Intentionally runs once on mount.
  }, []);

  const openPortal = async () => {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/portal/billing-portal`,
        { origin: window.location.origin },
        { headers: authHeader() },
      );
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const tier = user?.membership_tier;
  const tierLabel = tier ? tier[0].toUpperCase() + tier.slice(1) : 'No active plan';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-billing-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/members" className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center" data-testid="account-back-to-dashboard"><ArrowLeft className="w-4 h-4 mr-1" />Back to Member Dashboard</Link>
          <span className="font-semibold text-gray-900">Account & Billing</span>
          <span />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <UpdateProfileForm />

        <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">Subscription</h2>
            <div className="flex items-center gap-3">
              {syncing ? (
                <span className="text-xs text-gray-500 inline-flex items-center gap-1.5" data-testid="account-syncing">
                  <Loader2 className="w-3 h-3 animate-spin" />Syncing with Stripe...
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => runSync({ silent: false })}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="account-sync-now"
                >
                  Sync now
                </button>
              )}
            </div>
          </div>
          {syncFlash && (
            <div
              className={`mt-3 text-xs px-3 py-2 rounded-md border ${
                syncFlash.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-green-50 border-green-200 text-green-700'
              }`}
              data-testid="account-sync-flash"
            >
              {syncFlash.msg}
            </div>
          )}
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500">Current plan</dt><dd className="text-gray-900 font-medium" data-testid="account-tier-label">{tierLabel}</dd></div>
            {user?.membership_status && <div className="flex justify-between"><dt className="text-gray-500">Status</dt><dd className="text-gray-900 font-medium capitalize">{user.membership_status}</dd></div>}
            {user?.membership_end && <div className="flex justify-between"><dt className="text-gray-500">Renews</dt><dd className="text-gray-900 font-medium">{new Date(user.membership_end).toLocaleDateString()}</dd></div>}
          </dl>

          {error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          <div className="mt-6 space-y-3">
            {tier ? (
              <Button onClick={openPortal} disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white whitespace-normal text-center leading-snug py-3 h-auto" size="lg" data-testid="open-billing-portal-btn">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening Stripe portal...</>
                ) : (
                  <>
                    <span className="sm:hidden">Manage subscription</span>
                    <span className="hidden sm:inline">Manage subscription (upgrade, downgrade, cancel, update card)</span>
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={() => navigate('/#memberships')} className="w-full bg-blue-600 hover:bg-blue-700 text-white" size="lg">
                Choose a plan
              </Button>
            )}
          </div>
        </section>

        <ChangePasswordForm />

        <p className="text-xs text-gray-500 text-center">
          Subscription management is powered by Stripe. All sales are final. You can cancel at any time and access continues until the end of your current billing period.
        </p>
      </main>
      <HRHubFooter />
    </div>
  );
};
